/* oxlint-disable */
// Owns the registration API lifecycle only: calling Better Auth's signUp.email.
// UI orchestration (fingerprint collection, analytics, OTP sheet, notifications)
// stays in useRegistration. Session is owned by Better Auth useSession(); account
// hydration happens in AuthProvider when session.user.id changes.
import { useCallback, useState } from 'react';

import { Vendors } from '@/enums/core';

import { authClient } from '@/core/auth/client/auth-client';

export type RegisterState = 'idle' | 'pending' | 'success' | 'error';

export type RegisterPayload = {
  email: string;
  password: string;
  name?: string;
  remember?: boolean;
  parent?: number;
  vendor?: number;
  clickId?: string;
  gift?: string;
  affiliatorId?: string;
  partner?: string;
  landing?: number;
  phone?: string;
  currency?: string;
  country?: string; // 2-digit country code (GT, DO, GLOBAL, etc.)
  visitorId?: string | null; // Device fingerprint, validated server-side in lib/auth.ts
};

export type RegisterErrorState = {
  message: string;
  code?: string;
} | null;

// Fields accepted by Better Auth's typed signUp.email input.
type SignUpEmailArgs = Parameters<typeof authClient.signUp.email>[0];

// Custom registration fields are NOT part of the Better Auth user model —
// they are passed through to server hooks via ctx.body (see core/auth/server/config.ts).
// Keep them as an explicit intersection so we never fall back to blind `any`.
type RegistrationBodyExtras = {
  country?: string;
  vendor?: number;
  clickId?: string;
  gift?: string;
  affiliatorId?: string;
  partner?: string;
  landing?: number;
  visitorId?: string | null;
};

export const useRegister = () => {
  const [state, setState] = useState<RegisterState>('idle');
  const [error, setError] = useState<RegisterErrorState>(null);

  const register = useCallback(async (data: RegisterPayload) => {
    setState('pending');
    setError(null);

    try {
      // autoSignIn: true (core/auth/server/config.ts) creates a session after signup.
      // AuthProvider's useSession() + account sync effect pick it up reactively.
      const result = await authClient.signUp.email({
        email: data.email,
        password: data.password,
        name: data.name ?? '',
        currency: data.currency ?? 'USD',
        parent: data.parent ?? 0,
        phone: data.phone ?? '',
        country: data.country || 'GLOBAL',
        vendor: data.vendor ?? Vendors.EMAIL,
        clickId: data.clickId ?? '',
        gift: data.gift ?? '',
        affiliatorId: data.affiliatorId ?? '',
        partner: data.partner ?? '',
        landing: data.landing ?? 0,
        visitorId: data.visitorId ?? null,
      } as SignUpEmailArgs & RegistrationBodyExtras);

      if (result.error) {
        // Preserve error.code (e.g. DEVICE_BLOCKED) set by hooks.before so the UI can react to it.
        const registrationError = new Error(result.error.message || 'Registration failed') as Error & { code?: string };
        registrationError.code = (result.error as { code?: string }).code;
        throw registrationError;
      }

      setState('success');
      return result;
    } catch (caughtError) {
      const normalizedError = caughtError instanceof Error ? caughtError : new Error('Registration failed');

      setError({
        message: normalizedError.message,
        code: (normalizedError as Error & { code?: string }).code,
      });
      setState('error');
      throw normalizedError;
    }
  }, []);

  return { register, state, error };
};
